import { useEffect, useMemo, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { useHistory } from 'react-router-dom';

import '../pm/theme.css';
import { AppUser, OperatorRole } from '../pm/types';
import {
  displayName,
  OPERATOR_TAB_FILTERS,
} from '../pm/constants';
import { subscribeAllUsers } from '../pm/firebase';
import { getCurrentUser, readUser } from '../firebase';
import UserAvatar from '../components/pm/UserAvatar';
import OperatorRoleBadge from '../components/pm/OperatorRoleBadge';
import ManageOperatorMenu from '../components/pm/ManageOperatorMenu';
import InviteOperatorModal from '../components/pm/InviteOperatorModal';

const InternalTeam: React.FC = () => {
  const history = useHistory();
  const [authReady, setAuthReady] = useState(false);
  const [isOperator, setIsOperator] = useState(false);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [tab, setTab] = useState<string>('all');
  const [query, setQuery] = useState('');
  const [showInvite, setShowInvite] = useState(false);

  useEffect(() => {
    let active = true;
    getCurrentUser()
      .then(async (u: any) => {
        if (!active) return;
        if (u?.uid) {
          try {
            const data: any = await readUser(u.uid);
            if (active) setIsOperator(data?.restricted?.role === 'admin');
          } catch (e) { /* ignore */ }
        }
        if (active) setAuthReady(true);
      })
      .catch(() => { if (active) setAuthReady(true); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const unsub = subscribeAllUsers(setUsers);
    return unsub;
  }, []);

  const operators = useMemo(() => users.filter((u) => u.isOperator), [users]);

  const filtered = useMemo(() => {
    const filter = OPERATOR_TAB_FILTERS.find((f) => f.key === tab) || OPERATOR_TAB_FILTERS[0];
    const allowedRoles: OperatorRole[] | null = filter.roles.length ? filter.roles : null;
    const q = query.trim().toLowerCase();
    return operators.filter((u) => {
      if (allowedRoles && (!u.operatorRole || !allowedRoles.includes(u.operatorRole))) return false;
      if (q) {
        const name = displayName(u.email).toLowerCase();
        const roleLabel = u.operatorRole?.toLowerCase() || '';
        if (!name.includes(q) && !u.email.toLowerCase().includes(q) && !roleLabel.includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [operators, tab, query]);

  if (!authReady) {
    return <IonPage><IonContent fullscreen><div className="pm-app"><div className="pm-loading">Loading…</div></div></IonContent></IonPage>;
  }

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="pm-app">
          <div className="pm-page">
            <div className="pm-breadcrumb">
              <button type="button" className="pm-link-button" onClick={() => history.push('/Operator')}>Operator</button>
              {' / '}
              <span>Internal team</span>
            </div>

            <div className="pm-header pm-header-internal">
              <div className="pm-header-titles">
                <div className="pm-eyebrow">INTERNAL STAFF</div>
                <h1>Rollover Rep team</h1>
                <p>
                  People with operator-level access to the Rollover Rep platform.
                  Roles here are independent of any customer's own team roster.
                </p>
              </div>
              {isOperator ? (
                <div className="pm-header-actions">
                  <button
                    className="pm-button"
                    onClick={() => window.alert('SSO provisioning is not yet wired up — invite teammates manually for now.')}
                  >
                    Provision via SSO
                  </button>
                  <button
                    className="pm-button pm-button-primary"
                    onClick={() => setShowInvite(true)}
                  >
                    + Invite teammate
                  </button>
                </div>
              ) : null}
            </div>

            <div className="pm-filter-row">
              <div className="pm-search-wrap">
                <svg className="pm-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="11" cy="11" r="7" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  className="pm-search-input"
                  placeholder="Filter by name, role, or email…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <div className="pm-tabs">
                {OPERATOR_TAB_FILTERS.map((f) => (
                  <button
                    key={f.key}
                    className={`pm-tab ${tab === f.key ? 'active' : ''}`}
                    onClick={() => setTab(f.key)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="pm-member-table">
              <div className="pm-member-table-header">
                <div className="pm-member-col-member">MEMBER</div>
                <div className="pm-member-col-role">ROLE</div>
                <div className="pm-member-col-since">LAST ACTIVE</div>
                <div className="pm-member-col-actions" />
              </div>
              {filtered.length === 0 ? (
                <div className="pm-member-empty">
                  {operators.length === 0
                    ? 'No operators yet. Invite the first teammate above.'
                    : 'No operators match the current filter.'}
                </div>
              ) : (
                filtered.map((u) => (
                  <div key={u.uid} className="pm-member-row">
                    <div className="pm-member-col-member">
                      <UserAvatar email={u.email} size={32} />
                      <div className="pm-member-identity">
                        <div className="pm-member-name">{displayName(u.email)}</div>
                        <div className="pm-member-email">
                          {u.title ? <span>{u.title} · </span> : null}
                          {u.email}
                        </div>
                      </div>
                    </div>
                    <div className="pm-member-col-role">
                      <OperatorRoleBadge role={u.operatorRole} />
                    </div>
                    <div className="pm-member-col-since">
                      {/* last-active tracking not wired up yet — placeholder */}
                      <span className="pm-member-since-rel">—</span>
                    </div>
                    <div className="pm-member-col-actions">
                      <ManageOperatorMenu user={u} disabled={!isOperator} />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        {showInvite ? (
          <InviteOperatorModal
            allUsers={users}
            onClose={() => setShowInvite(false)}
          />
        ) : null}
      </IonContent>
    </IonPage>
  );
};

export default InternalTeam;
